import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import { createPortalSession } from '../subscription/subscriptionApi';
import { formatRenewalDate } from '../subscription/subscriptionPlans';
import { useSubscription } from '../subscription/useSubscription';
import { updateSettings, UserSettings } from './settingsApi';
import ToggleSetting from './ToggleSetting';
import { useUserSettings } from './useUserSettings';
import { BoardThemePicker } from './BoardThemePicker';
import {
  normalizeRefutationWaitMs,
  readRefutationWaitMs,
  writeRefutationWaitMs,
} from './refutationWaitSetting';
import {
  settingsBodyTextSx,
  settingsSectionPaperSx,
  settingsSectionTitleSx,
  settingsShellSx,
  settingsTitleSx,
} from './settingsMuiStyles';

const SettingsPage = () => {
  const { subscription, loading: subscriptionLoading } = useSubscription();
  const { settings, loading, applySettings } = useUserSettings();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');
  const [refutationWaitMs, setRefutationWaitMs] = useState(
    readRefutationWaitMs,
  );

  const isProAccount = subscription.isPro || subscription.isLifetimeMember;
  const canManageBilling = subscription.isPro && !subscription.isLifetimeMember;
  const planLabel = isProAccount ? 'Pro' : 'Free';
  const renewalLabel = formatRenewalDate(subscription.currentPeriodEnd);

  const handleChange = async <K extends keyof UserSettings>(
    key: K,
    value: UserSettings[K],
  ) => {
    const previous = settings;
    applySettings({ ...settings, [key]: value });
    setSaveError('');
    setSaving(true);
    try {
      const saved = await updateSettings({ [key]: value }, {
        source: 'settings_page',
      });
      applySettings(saved);
    } catch (error) {
      applySettings(previous);
      setSaveError(
        error instanceof Error ? error.message : 'Failed to save settings',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalError('');
    setPortalLoading(true);
    try {
      const portalUrl = await createPortalSession();
      window.location.href = portalUrl;
    } catch (error) {
      setPortalError(
        error instanceof Error ? error.message : 'Failed to open billing portal',
      );
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <Box sx={settingsShellSx}>
      <Typography variant="h4" gutterBottom sx={settingsTitleSx}>
        Settings
      </Typography>

      <Paper variant="outlined" sx={settingsSectionPaperSx}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="h6" sx={settingsSectionTitleSx}>
            Subscription
          </Typography>
          {subscriptionLoading ? (
            <CircularProgress size={20} />
          ) : (
            <Chip
              label={planLabel}
              color={isProAccount ? 'primary' : 'default'}
              size="medium"
            />
          )}
        </Stack>

        {subscriptionLoading ? (
          <Typography variant="body2" color="text.secondary" sx={settingsBodyTextSx}>
            Loading subscription…
          </Typography>
        ) : (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ...settingsBodyTextSx, mb: 2 }}
            >
              {isProAccount ? (
                subscription.isLifetimeMember ? (
                  'You have lifetime Pro access.'
                ) : renewalLabel ? (
                  `Your Pro subscription renews on ${renewalLabel}.`
                ) : (
                  'Your Pro subscription is active.'
                )
              ) : subscription.isTrial ? (
                <>
                  You are on the Free plan with full access during your first
                  three months
                  {subscription.trialEndsAt
                    ? ` until ${formatRenewalDate(subscription.trialEndsAt)}`
                    : ''}
                  . After that, the free plan includes 5 puzzles and 5 SRS
                  reviews per day, full explorer access, and one new replay game
                  every 3 days.
                </>
              ) : (
                <>
                  You are on the Free plan: 5 puzzles and 5 SRS reviews per day,
                  full explorer access, and one new replay game every 3 days.
                </>
              )}
            </Typography>

            {portalError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {portalError}
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {canManageBilling && (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={portalLoading}
                  onClick={() => void handleManageBilling()}
                >
                  {portalLoading ? 'Opening…' : 'Manage billing'}
                </Button>
              )}
              {!isProAccount && (
                <Button
                  component={RouterLink}
                  to="/subscribe"
                  variant="contained"
                  color="primary"
                >
                  Upgrade to Pro
                </Button>
              )}
              <Button component={RouterLink} to="/subscribe" variant="outlined">
                View plans
              </Button>
            </Stack>
          </>
        )}
      </Paper>

      <Paper variant="outlined" sx={settingsSectionPaperSx}>
        <Typography variant="h6" gutterBottom sx={settingsSectionTitleSx}>
          Board
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ...settingsBodyTextSx, mb: 3 }}
        >
          Choose the square colors used on chessboards across puzzles, replay,
          explorer, and review.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={4}>
            <BoardThemePicker
              value={settings.boardTheme}
              disabled={saving}
              onChange={(value) => handleChange('boardTheme', value)}
            />
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={settingsSectionPaperSx}>
        <Typography variant="h6" gutterBottom sx={settingsSectionTitleSx}>
          Spaced repetition
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ...settingsBodyTextSx, mb: 3 }}
        >
          Automatically build your review decks from the mistakes you make while
          solving puzzles.
        </Typography>

        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={4}>
            <ToggleSetting
              id="add-missed-puzzles"
              label="Add missed puzzles to SRS"
              helper="When you fail a puzzle, add it to review starting at the missed move."
              value={settings.addMissedPuzzlesToSrs}
              disabled={saving}
              onChange={(value) => handleChange('addMissedPuzzlesToSrs', value)}
            />
            <ToggleSetting
              id="add-missed-positions"
              label="Add missed positions to SRS"
              helper="When you play a wrong move, add that exact position to your review deck."
              value={settings.addMissedPositionsToSrs}
              disabled={saving}
              onChange={(value) =>
                handleChange('addMissedPositionsToSrs', value)
              }
            />
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={settingsSectionPaperSx}>
        <Typography variant="h6" gutterBottom sx={settingsSectionTitleSx}>
          Training
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ...settingsBodyTextSx, mb: 3 }}
        >
          Experimental preferences saved only on this device.
        </Typography>

        <Box>
          <Typography id="refutation-wait-slider-label" gutterBottom>
            Refutation wait
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ...settingsBodyTextSx, mb: 1.5 }}
          >
            Choose how long the trainer waits for a refutation after a wrong
            move.
          </Typography>
          <Slider
            aria-labelledby="refutation-wait-slider-label"
            min={0.25}
            max={1}
            step={0.25}
            marks={[
              { value: 0.25, label: '0.25s' },
              { value: 0.5, label: '0.5s' },
              { value: 0.75, label: '0.75s' },
              { value: 1, label: '1s' },
            ]}
            value={refutationWaitMs / 1000}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value}s`}
            onChange={(_, value) => {
              const seconds = Array.isArray(value) ? value[0] : value;
              setRefutationWaitMs(normalizeRefutationWaitMs(seconds * 1000));
            }}
            onChangeCommitted={(_, value) => {
              const seconds = Array.isArray(value) ? value[0] : value;
              const waitMs = normalizeRefutationWaitMs(seconds * 1000);
              setRefutationWaitMs(waitMs);
              writeRefutationWaitMs(waitMs);
            }}
            sx={{ maxWidth: 420 }}
          />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={settingsSectionPaperSx}>
        <Typography variant="h6" gutterBottom sx={settingsSectionTitleSx}>
          Replay
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ...settingsBodyTextSx, mb: 3 }}
        >
          Control replay hub layout and what happens after a wrong move while
          drilling a game.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <ToggleSetting
              id="auto-show-top-replay-game"
              label="Auto-show first game"
              value={settings.autoShowTopReplayGame}
              disabled={saving}
              onChange={(value) =>
                handleChange('autoShowTopReplayGame', value)
              }
            />
            <ToggleSetting
              id="auto-show-replay-wrong-moves"
              label="Show best move after a miss"
              helper='When on (default), the best-move arrow appears after the refutation and you drag it. When off, you retry without the arrow — up to 2 misses per move.'
              value={settings.autoShowReplayWrongMoves}
              disabled={saving}
              onChange={(value) =>
                handleChange('autoShowReplayWrongMoves', value)
              }
            />
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={settingsSectionPaperSx}>
        <Typography variant="h6" gutterBottom sx={settingsSectionTitleSx}>
          Help
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ...settingsBodyTextSx, mb: 2 }}
        >
          Found a bug or have feedback? Send us a message.
        </Typography>
        <Button component={RouterLink} to="/contact" variant="outlined">
          Contact us
        </Button>
      </Paper>

      <Box sx={{ mt: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: { xxl: '0.8125rem', xxxl: '0.875rem' } }}
        >
          Account preferences require sign-in; experimental device preferences
          are saved locally.
        </Typography>
      </Box>
    </Box>
  );
};

export default SettingsPage;
